import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, RotateCcw, Shield, Truck, Award, IndianRupee } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLLECTION_VISUALS = {
  best_sellers: 'https://images.unsplash.com/photo-1635767798638-3e25273a8236?w=900&q=80',
  bridal: 'https://images.unsplash.com/photo-1620656798932-902402be15a4?w=900&q=80',
  everyday_essentials: 'https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=900&q=80',
  festive: 'https://images.unsplash.com/photo-1615655406736-b37c4fabf923?w=900&q=80',
  gifting: 'https://images.unsplash.com/photo-1584302179602-e4c3d3fd629d?w=900&q=80',
  minimalist: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=900&q=80',
  new_arrivals: 'https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=900&q=80',
  statement_pieces: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=900&q=80',
  trending: 'https://images.unsplash.com/photo-1635767798638-3e25273a8236?w=900&q=80',
};

const Home = () => {
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/collections`)
      .then(({ data }) => setCollections(data.slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#E4E8DF]">
      {/* Hero Section - Split Layout */}
      <section className="min-h-[80vh] md:min-h-screen flex items-center px-4 md:px-6 pt-24 lg:pt-0 pb-12 lg:pb-0">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif tracking-tight text-[#2D3B26] leading-none mb-4 md:mb-6">
              Less is<br /><span className="italic text-[#C27054]">More</span>
            </h1>
            <p className="text-base md:text-lg text-[#475740] leading-relaxed mb-6 md:mb-8 max-w-lg">
              Minimal jewelry that speaks volumes. Clean lines, pure metals, timeless design for the modern generation.
            </p>
            <div className="flex flex-wrap gap-3 md:gap-4">
              <Link
                to="/products"
                data-testid="hero-shop-now-button"
                className="bg-[#C27054] text-white rounded-full px-6 md:px-10 py-3 md:py-4 hover:bg-[#A85B42] transition-all duration-300 text-xs md:text-sm uppercase tracking-widest font-semibold shadow-[0_4px_14px_0_rgba(194,112,84,0.39)]"
              >
                Shop Now
              </Link>
              <Link
                to="/virtual-tryon"
                data-testid="hero-virtual-tryon-button"
                className="border border-[#2D3B26] text-[#2D3B26] rounded-full px-6 md:px-10 py-3 md:py-4 hover:bg-[#2D3B26] hover:text-[#E4E8DF] transition-all duration-300 text-xs md:text-sm uppercase tracking-widest font-semibold"
              >
                Try On
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative aspect-[3/4] rounded-3xl overflow-hidden order-first lg:order-last max-h-[50vh] lg:max-h-none"
          >
            <img
              src="https://images.unsplash.com/photo-1598218940656-7126545fd283?w=800&q=80"
              alt="Jewelry"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Gold Collection - Full Width */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <Link to="/products?category=gold" data-testid="category-gold-card">
            <motion.div
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.3 }}
              className="relative aspect-[21/9] overflow-hidden rounded-3xl group cursor-pointer"
            >
              <img
                src="https://images.unsplash.com/photo-1689775377968-f55588175af6?w=1600&q=80"
                alt="Gold Collection"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#2D3B26]/80 via-[#2D3B26]/30 to-transparent flex flex-col items-start justify-center px-6 md:pl-20 md:pr-12">
                <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#C27054] font-semibold mb-2 md:mb-3">Explore Collection</p>
                <h3 className="text-2xl md:text-4xl lg:text-6xl font-serif text-[#FDFBF7] mb-2 md:mb-4">Gold Collection</h3>
                <p className="text-xs md:text-base text-[#E4E8DF]/80 mb-3 md:mb-6 max-w-md hidden sm:block">Modern gold pieces crafted for the new generation. Minimal, bold, unforgettable.</p>
                <span className="inline-flex items-center gap-2 bg-[#C27054] text-white rounded-full px-5 md:px-8 py-2 md:py-3 text-xs md:text-sm uppercase tracking-widest font-semibold hover:bg-[#A85B42] transition-colors">
                  Shop Now <ArrowRight size={14} />
                </span>
              </div>
            </motion.div>
          </Link>
        </div>
      </section>

      {/* Shop by Category */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-[#E4E8DF]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#C27054] mb-4">
              Browse By Category
            </p>
            <h2 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26]">
              Shop by Category
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {[
              { name: 'Ring', type: 'ring', image: 'https://images.unsplash.com/photo-1655255114527-d0a834d9a774?w=600&q=80' },
              { name: 'Earring', type: 'earring', image: 'https://images.unsplash.com/photo-1566583188380-1ca219c0b873?w=600&q=80' },
              { name: 'Pendant', type: 'pendant', image: 'https://images.unsplash.com/photo-1767391255584-763f98ced9d0?w=600&q=80' },
              { name: 'Bracelet', type: 'bracelet', image: 'https://images.unsplash.com/photo-1767391247332-23df72115e32?w=600&q=80' },
              { name: 'Mangalsutra', type: 'mangalsutra', image: 'https://images.unsplash.com/photo-1750492272603-43888a3fee4d?w=600&q=80' },
              { name: 'Necklace', type: 'necklace', image: 'https://images.unsplash.com/photo-1689775381466-a1d2f6cc0944?w=600&q=80' },
            ].map((cat, index) => (
              <Link to={`/products?type=${cat.type}`} key={cat.type} data-testid={`home-category-${cat.type}`}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative overflow-hidden rounded-2xl group cursor-pointer bg-white shadow-[0_4px_20px_rgba(45,59,38,0.08)] hover:shadow-[0_8px_30px_rgba(45,59,38,0.15)] transition-shadow duration-300"
                >
                  <div className="aspect-square bg-[#fafaf8] p-8 flex items-center justify-center overflow-hidden">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="py-4 text-center bg-white">
                    <h3 className="text-lg font-serif text-[#2D3B26] group-hover:text-[#C27054] transition-colors">{cat.name}</h3>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Curated Collections */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8 md:mb-12 flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#C27054] mb-2 md:mb-3">Curated For You</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26]">
                Shop The Collections
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="home-view-all-collections"
              className="text-xs md:text-sm uppercase tracking-widest text-[#C27054] font-semibold hover:underline flex items-center gap-1"
            >
              View All <ArrowRight size={14} />
            </Link>
          </div>

          {collections.length === 0 ? (
            <p className="text-sm text-[#7A8A73] italic">Collections coming soon.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
              {collections.map((col, index) => (
                <Link
                  to={`/products?collection=${col.slug}`}
                  key={col.id}
                  data-testid={`home-collection-${col.slug}`}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="relative aspect-[4/5] overflow-hidden rounded-2xl group cursor-pointer bg-[#E4E8DF] shadow-[0_4px_20px_rgba(45,59,38,0.08)] hover:shadow-[0_12px_40px_rgba(45,59,38,0.18)] transition-shadow duration-500"
                  >
                    <img
                      src={COLLECTION_VISUALS[col.slug] || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=900&q=80'}
                      alt={col.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2D3B26]/85 via-[#2D3B26]/30 to-transparent flex flex-col items-start justify-end p-4 md:p-6">
                      <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#C27054] font-semibold mb-1 md:mb-2">Collection</p>
                      <h3 className="text-lg md:text-2xl lg:text-3xl font-serif text-[#FDFBF7] mb-2 md:mb-3 leading-tight">
                        {col.name}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-[10px] md:text-xs uppercase tracking-widest text-[#FDFBF7]/90 font-medium border-b border-[#C27054] pb-0.5 group-hover:border-[#FDFBF7] transition-colors">
                        Explore <ArrowRight size={12} />
                      </span>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Try Before You Buy - Full Width Modern */}
      <section className="relative bg-[#2D3B26] overflow-hidden">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Left - Images Grid */}
            <div className="lg:col-span-3 grid grid-cols-2 gap-1">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="aspect-square overflow-hidden"
              >
                <img src="https://images.unsplash.com/photo-1717766639730-7bc5c768961e?w=600&q=80" alt="Try on" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="aspect-square overflow-hidden"
              >
                <img src="https://images.unsplash.com/photo-1598218940656-7126545fd283?w=600&q=80" alt="Try on" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="aspect-square overflow-hidden"
              >
                <img src="https://images.unsplash.com/photo-1743264385411-57c883bdc0ea?w=600" alt="Try on" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="aspect-square overflow-hidden"
              >
                <img src="https://images.pexels.com/photos/36730425/pexels-photo-36730425.jpeg?auto=compress&w=600" alt="Try on" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
            </div>

            {/* Right - Content */}
            <div className="lg:col-span-2 flex items-center px-8 lg:px-16 py-16 lg:py-0">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#C27054] mb-4">AI Virtual Try-On</p>
                <h2 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#FDFBF7] leading-[1.1] mb-6">
                  Try Before<br />You Buy
                </h2>
                <p className="text-base text-[#E4E8DF]/70 leading-relaxed mb-4">
                  Upload your photo and see exactly how every piece looks on you. No guesswork, no surprises.
                </p>
                <div className="space-y-3 mb-8">
                  {['Upload your selfie', 'Pick any jewelry', 'See it on you instantly'].map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#C27054] flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">{i + 1}</span>
                      </div>
                      <span className="text-sm text-[#E4E8DF]/80">{step}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/virtual-tryon"
                  data-testid="banner-virtual-tryon-button"
                  className="inline-block bg-[#C27054] text-white rounded-full px-10 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_20px_0_rgba(194,112,84,0.4)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold"
                >
                  Try It Free
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
          {[
            { icon: 'RotateCcw', title: '30 Days Return', desc: 'Easy & hassle-free returns' },
            { icon: 'Shield', title: 'Lifetime Warranty', desc: 'On all our products' },
            { icon: 'IndianRupee', title: 'Lifetime Buyback', desc: '100% fair-value buyback' },
            { icon: 'Truck', title: 'Free Delivery', desc: 'Pan India shipping' },
            { icon: 'Award', title: 'Hallmark Certified', desc: 'BIS certified jewelry' },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              viewport={{ once: true }}
              className="text-center"
              data-testid={`trust-badge-${i}`}
            >
              <div className="w-16 h-16 bg-[#E4E8DF] rounded-full flex items-center justify-center mx-auto mb-5">
                {item.icon === 'RotateCcw' && <RotateCcw size={24} className="text-[#C27054]" />}
                {item.icon === 'Shield' && <Shield size={24} className="text-[#C27054]" />}
                {item.icon === 'IndianRupee' && <IndianRupee size={24} className="text-[#C27054]" />}
                {item.icon === 'Truck' && <Truck size={24} className="text-[#C27054]" />}
                {item.icon === 'Award' && <Award size={24} className="text-[#C27054]" />}
              </div>
              <h3 className="text-lg font-serif text-[#2D3B26] mb-2">{item.title}</h3>
              <p className="text-sm text-[#475740] leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
